import React, { createContext, useContext, useEffect, useRef, useState } from 'react';

const TwitchStatusContext = createContext(null);

const STATUS_ENDPOINT = process.env.REACT_APP_TWITCH_STATUS_ENDPOINT || '/api/twitch/status';
const POLL_MS = 30000;

function computeUptime(startIso) {
  if (!startIso) return null;
  const diffMs = Date.now() - new Date(startIso).getTime();
  const hours = Math.floor(diffMs / 3600000);
  const minutes = Math.floor((diffMs % 3600000) / 60000);
  return `${hours}h ${minutes}m`;
}

async function fetchWithTimeout(url, timeout = 7000) {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeout);
  try {
    return await fetch(url, { signal: controller.signal });
  } finally {
    clearTimeout(id);
  }
}

const OFFLINE = { isLive: false, viewerCount: null, category: null, startedAt: null };

// Polls the Twitch status endpoint once for the whole app so the sidebar
// and the home page share a single request loop.
export function TwitchStatusProvider({ children }) {
  // isLive: null while the first check is in flight
  const [status, setStatus] = useState({ ...OFFLINE, isLive: null });
  const [uptime, setUptime] = useState(null);
  const startedAtRef = useRef(null);

  useEffect(() => {
    let mounted = true;

    async function checkLive() {
      let next = OFFLINE;
      try {
        const res = await fetchWithTimeout(STATUS_ENDPOINT);
        if (res.ok) {
          const data = await res.json();
          if (data.isLive && data.stream) {
            next = {
              isLive: true,
              viewerCount: typeof data.stream.viewer_count === 'number' ? data.stream.viewer_count : null,
              category: data.stream.category ?? null,
              startedAt: data.stream.started_at ?? null,
            };
          } else if (data.isLive) {
            next = { ...OFFLINE, isLive: true };
          }
        }
      } catch {
        // treat network errors as offline
      }
      if (!mounted) return;
      startedAtRef.current = next.startedAt;
      setStatus(next);
      setUptime(computeUptime(next.startedAt));
    }

    checkLive();
    const pollInterval = setInterval(checkLive, POLL_MS);
    const uptimeInterval = setInterval(() => {
      if (startedAtRef.current) setUptime(computeUptime(startedAtRef.current));
    }, 60000);

    return () => {
      mounted = false;
      clearInterval(pollInterval);
      clearInterval(uptimeInterval);
    };
  }, []);

  return (
    <TwitchStatusContext.Provider value={{ ...status, uptime }}>
      {children}
    </TwitchStatusContext.Provider>
  );
}

export function useTwitchStatus() {
  return useContext(TwitchStatusContext) || { ...OFFLINE, isLive: null, uptime: null };
}
